"use client";

import { useEffect, useRef } from "react";
import type { GpxFieldValue } from "../utils/gpxValue";
import { gpxLastFix } from "../utils/gpxValue";

type Props = {
  open: boolean;
  value: GpxFieldValue | null;
  title?: string;
  onClose: () => void;
};

export function GpxMapModal({ open, value, title, onClose }: Props) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    if (!open || !value || !iframeRef.current?.contentWindow) return;
    const last = gpxLastFix(value);
    if (!last) return;
    const js = `
      if (window._gpxMap) {
        var pt = [${last.lat}, ${last.lon}];
        window._gpxPolyline.addLatLng(pt);
        window._gpxLiveMarker.setLatLng(pt);
        window._gpxMap.setView(pt, window._gpxMap.getZoom());
      }
    `;
    try {
      iframeRef.current.contentWindow.postMessage({ type: "gpx-update", js }, "*");
    } catch {
      // ignore
    }
  }, [open, value]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <h2 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
            {title ?? "GPS Track"}
          </h2>
          <button
            type="button"
            className="rounded px-2 py-1 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        {!value || value.track.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-sm text-slate-500">
            No track points recorded yet.
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            title={title ?? "GPS Track"}
            className="h-full w-full flex-1 border-0"
            srcDoc={buildMapHtml(value)}
          />
        )}
        {value?.isRecording ? (
          <div className="flex items-center gap-2 border-t border-orange-200 bg-orange-50 px-4 py-2 text-sm text-orange-800">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
            Live — {value.track.length} pts
          </div>
        ) : null}
      </div>
    </div>
  );
}

function buildMapHtml(value: GpxFieldValue | null): string {
  const track = value?.track ?? [];
  const last = track.length > 0 ? track[track.length - 1] : null;
  const center = last ? [last.lat, last.lon] : [51.505, -0.09];
  const zoom = last ? 15 : 4;
  const coordsJson = JSON.stringify(track.map((p) => [p.lat, p.lon]));

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
  <style>html, body, #map { margin: 0; padding: 0; width: 100%; height: 100%; }</style>
</head>
<body>
<div id="map"></div>
<script>
  var coords = ${coordsJson};
  var map = L.map('map').setView([${center[0]}, ${center[1]}], ${zoom});
  window._gpxMap = map;
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19
  }).addTo(map);
  var polyline = L.polyline(coords, { color: '#1976d2', weight: 3 }).addTo(map);
  window._gpxPolyline = polyline;
  if (coords.length > 0) {
    map.fitBounds(polyline.getBounds(), { padding: [20, 20] });
    L.circleMarker(coords[0], { radius: 6, color: '#388e3c', fillColor: '#66bb6a', fillOpacity: 1 })
      .bindPopup('Start').addTo(map);
    var last = coords[coords.length - 1];
    window._gpxLiveMarker = L.circleMarker(last, { radius: 8, color: '#e53935', fillColor: '#ef5350', fillOpacity: 1 })
      .bindPopup('Last fix').addTo(map);
  } else {
    window._gpxLiveMarker = L.circleMarker([${center[0]}, ${center[1]}], {
      radius: 8, color: '#e53935', fillColor: '#ef5350', fillOpacity: 0
    }).addTo(map);
    window._gpxPolyline = L.polyline([], { color: '#1976d2', weight: 3 }).addTo(map);
  }
  window.addEventListener('message', function (event) {
    if (event.data && event.data.type === 'gpx-update' && event.data.js) {
      try { eval(event.data.js); } catch (e) {}
    }
  });
</script>
</body>
</html>`;
}
